/**
 * PromThankYouPage — /prom/thank-you
 *
 * High-conversion confirmation: removes post-submit anxiety, sets
 * expectation, and creates momentum. Copy is service-led ("we're
 * setting it up now") so the teacher feels a real relationship is
 * starting, not a form was filed.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackGrowth } from '../lib/growthTrack';

export default function PromThankYouPage() {
  const navigate = useNavigate();

  useEffect(() => {
    trackGrowth('prom_thank_you_viewed');
  }, []);

  const handleSampleClick = () => {
    trackGrowth('prom_demo_clicked');
    navigate('/e/demo-prom');
  };

  return (
    <div
      className="bg-neutral-950 text-white min-h-screen flex items-center justify-center px-6 py-12"
      data-testid="prom-thank-you-page"
    >
      <div className="max-w-md w-full text-center">
        <div className="text-5xl mb-4" aria-hidden="true">🎉</div>

        <h1 className="text-2xl font-semibold mb-2" data-testid="prom-thank-you-title">
          You&apos;re all set
        </h1>

        <p className="text-neutral-400 mb-6" data-testid="prom-thank-you-subhead">
          We&apos;re setting up your prom page now.
        </p>

        <p className="text-sm text-neutral-300 mb-6" data-testid="prom-thank-you-key-line">
          You&apos;ll receive your QR code and live link shortly — nothing else needed from you.
        </p>

        {/* Steps */}
        <div className="bg-neutral-900 rounded-xl p-4 text-left mb-6" data-testid="prom-thank-you-next-steps">
          <div className="font-medium mb-2">What happens next</div>
          <ul className="text-sm text-neutral-400 space-y-1">
            <li>1. We create your prom page</li>
            <li>2. We generate your QR code</li>
            <li>3. We send everything to your email</li>
          </ul>
          <div className="text-xs text-neutral-500 mt-3">
            Usually within a few hours
          </div>
        </div>

        {/* Preview — keeps momentum, builds confidence */}
        <p className="text-sm text-neutral-400 mb-2">Want to see how it works?</p>
        <button
          onClick={handleSampleClick}
          className="w-full bg-white text-black py-3 rounded-xl mb-3 font-medium hover:bg-neutral-200 transition-colors"
          data-testid="prom-thank-you-sample-btn"
        >
          View a sample prom →
        </button>

        {/* Reassurance */}
        <div className="text-xs text-neutral-400 mt-6" data-testid="prom-thank-you-reassurance">
          No apps. No logins. Students just scan and start.
        </div>

        {/* Contact + edits loop */}
        <div className="text-xs text-neutral-500 mt-4" data-testid="prom-thank-you-contact">
          Need anything specific? Just reply to the email you receive — we&apos;ll handle it.
        </div>
        <div className="text-xs text-neutral-500 mt-2" data-testid="prom-thank-you-edits">
          If your date changes or you want edits, we can update everything instantly.
        </div>
      </div>
    </div>
  );
}
